import { onBeforeUnmount, ref, watch } from "vue";
import type { Ref } from "vue";
import BadgeApi from "@/apis/badges";
import type { BadgeAction, UserBadge } from "@/apis/badges";

export function useBadgeDetail(userBadgeId: Ref<string>): {
  badge: Ref<UserBadge | null>;
  loading: Ref<boolean>;
  error: Ref<string>;
  actionPending: Ref<boolean>;
  refresh: () => Promise<void>;
  changeWearing: (action: BadgeAction) => Promise<void>;
} {
  const badge = ref<UserBadge | null>(null);
  const loading = ref(false);
  const error = ref("");
  const actionPending = ref(false);
  let controller: AbortController | null = null;
  let disposed = false;

  async function refresh() {
    controller?.abort();
    const request = new AbortController();
    controller = request;
    const id = userBadgeId.value;
    error.value = "";

    if (!id) {
      badge.value = null;
      loading.value = false;
      error.value = "缺少徽章 ID";
      return;
    }

    loading.value = true;
    try {
      const res = await BadgeApi.getDetail(id, request.signal);
      if (request.signal.aborted || disposed) return;
      if (String(res.code) !== "0" || !res.data?.badge) {
        throw new Error(res.message || "徽章不存在或暂时无法查看");
      }
      badge.value = res.data.badge;
    } catch (err) {
      if (request.signal.aborted || disposed) return;
      error.value = err instanceof Error ? err.message : "加载失败，请重试";
    } finally {
      if (!request.signal.aborted && !disposed) loading.value = false;
    }
  }

  async function changeWearing(action: BadgeAction) {
    if (actionPending.value || !badge.value?.ownerId || !userBadgeId.value) {
      return;
    }
    const id = userBadgeId.value;
    actionPending.value = true;
    try {
      const res = await BadgeApi.action({
        id,
        ownerId: badge.value.ownerId,
        action,
      });
      if (String(res.code) !== "0") {
        throw new Error(res.message || "操作失败，请重试");
      }
      if (!disposed && userBadgeId.value === id) {
        // 保留已成功的操作结果；刷新失败时仍可继续操作或手动重试。
        if (badge.value) badge.value.isWearing = action === "WEARING";
        await refresh();
      }
    } finally {
      if (!disposed) actionPending.value = false;
    }
  }

  watch(
    userBadgeId,
    () => {
      badge.value = null;
      refresh();
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    disposed = true;
    controller?.abort();
  });

  return { badge, loading, error, actionPending, refresh, changeWearing };
}
